import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone } from "@angular/core";
import { environment } from "../../environments/environment";
import { Observable, from, of, BehaviorSubject } from "rxjs";
import { mergeMap, delay, retryWhen, map, catchError } from "rxjs/operators";

// Constante para a chave do token no SessionStorage
const TOKEN_STORAGE_KEY = "encodedAccessToken"; // Ajuste de acordo com a chave real usada no seu sistema
const MAX_TOKEN_WAIT_TIME = 30000; // Tempo máximo de espera pelo token (30 segundos)
const TOKEN_CHECK_INTERVAL = 500; // Intervalo para verificar o token (500ms)

export interface ChatFeedback {
    rating: number;
    comment?: string;
    timestamp: Date;
    messageIndex: number;
}

export interface ChatSummary {
    id: string;
    preview: string;
    isExpired?: boolean;
}

export interface SimplifiedConversation {
    _id: string;
    user: string;
    messages: Message[]
    feedbacks?: ChatFeedback[];
}

export interface ToolCall {
    id: string;
    name: string;
    args: any;
    result?: any;
    error?: string;
    isExpanded?: boolean;
    status: 'pending' | 'success' | 'error';
}

export interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp?: Date;
    feedback?: ChatFeedback;
    index?: number;
    feedbackDone?: boolean;
    isStreaming?: boolean; // Indica se a mensagem está recebendo streaming
    isComplete?: boolean;  // Indica se a mensagem foi completamente recebida
    toolCalls?: ToolCall[]; // Chamadas de tools durante a mensagem
    error?: string; // Erro geral da mensagem
}

export interface AgentRunRequest {
    appName: string;
    userId: string;
    sessionId: string;
    newMessage: {
        role: 'user' | 'assistant';
        parts: Array<{
            text?: string;
            inlineData?: {
                mimeType: string;
                data: string;
                displayName?: string;
            };
        }>;
    };
    state_delta?: any;
    streaming: boolean;
}

// Interface para dados de arquivo anexado
export interface FileAttachment {
    name: string;
    type: string;
    size: number;
    base64: string;
}

// Interface para dados recebidos via SSE
export interface SSEChunk {
    type?: 'content' | 'metadata' | 'error' | 'done';
    content?: string;
    chatid?: string;
    metadata?: any;
    error?: string;
}

export const knowledgeBaseName = "githubcom_ford-innersource_wings-sales-specialist";

@Injectable({providedIn: "root"})
export class WingsAssistantChatService {
    // URL base para endpoints do LangGraph (histórico e feedback)
    private chatHistoryUrl: string = environment.chatAgentUrl + "/api";
    private agentName = "wings_agent"
    private adkUrl : string =  environment.chatAdkUrl;
    // URL base para o novo endpoint SSE (Google ADK -> Langchain via A2A e MCPs)
    private apiServerDomain: string = environment.chatAgentUrl; // Ajuste conforme necessário
    
    // BehaviorSubject para controlar estado de loading
    public isLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    
    constructor(
        private httpService: HttpClient,
        private zone: NgZone
    ) {}
    
    /**
     * Verifica se o token está disponível no SessionStorage
     * @returns True se o token estiver disponível, False caso contrário
     */
    private isTokenAvailable(): boolean {
        try {
            const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
            return !!token && token.length > 0;
        } catch (error) {
            console.error('Erro ao verificar token no SessionStorage:', error);
            return false;
        }
    }
    
    /**
     * Aguarda até que o token esteja disponível no SessionStorage
     * @returns Promise que resolve quando o token está disponível ou rejeita após timeout
     */
    private waitForToken(): Promise<boolean> {
        // Retorna imediatamente se o token já estiver disponível
        if (this.isTokenAvailable()) {
            return Promise.resolve(true);
        }
        
        console.log('Aguardando token no SessionStorage...');
        
        return new Promise<boolean>((resolve, reject) => {
            let elapsedTime = 0;
            
            const checkInterval = setInterval(() => {
                if (this.isTokenAvailable()) {
                    clearInterval(checkInterval);
                    console.log('Token encontrado no SessionStorage');
                    resolve(true);
                    return;
                }
                
                elapsedTime += TOKEN_CHECK_INTERVAL;
                if (elapsedTime >= MAX_TOKEN_WAIT_TIME) {
                    clearInterval(checkInterval);
                    console.error(`Timeout aguardando token (${MAX_TOKEN_WAIT_TIME}ms)`);
                    resolve(true);
                    return;
                }
            }, TOKEN_CHECK_INTERVAL);
        });
    }

    /**
     * Método utilitário para envolver chamadas HTTP e garantir que o token esteja disponível antes de prosseguir
     * @param apiCall Função que realiza a chamada HTTP
     * @returns Promise com o resultado da chamada HTTP
     */
    private async withToken<T>(apiCall: () => Promise<T>): Promise<T> {
        try {
            // Aguardar o token antes de prosseguir
            await this.waitForToken();
            // Executar a chamada API após o token estar disponível
            return await apiCall();
        } catch (error) {
            console.error('Erro na operação com token:', error);
            throw error;
        }
    }

    /**
     * Obtém o token de autenticação do SessionStorage
     * @returns Token de autenticação ou null se não disponível
     */
    public getAuthToken(): string | null {
        try {
            return sessionStorage.getItem(TOKEN_STORAGE_KEY);
        } catch (error) {
            console.error('Erro ao obter token de autenticação:', error);
            return null;
        }
    }

    /**
     * Inicializa uma sessão no ADK (Agent Development Kit)
     * @param userId ID do usuário
     * @param sessionId ID da sessão
     * @returns Promise que resolve true se a sessão foi inicializada com sucesso
     */
    public async startSession(userId: string, sessionId: string): Promise<boolean> {
        return this.withToken(async () => {
            try {
                const token = this.getAuthToken();
                if (!token) {
                    throw new Error('Token de autenticação não disponível');
                }

                const url = `${this.adkUrl}/apps/${this.agentName}/users/${userId}/sessions/${sessionId}`;
                
                const response = await this.httpService.post(url, {
                    sessionId: sessionId
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                }).toPromise();
                
                console.log(`Sessão ${sessionId} inicializada com sucesso para usuário ${userId}`);
                return true;
                
            } catch (error) {
                // Tratar erro 400 com mensagem "Session already exists" como sucesso
                if (error && error.status === 400 && error.error && error.error.detail && error.error.detail.includes('Session already exists')) {
                    console.log(`Sessão ${sessionId} já existe - continuando normalmente`);
                    return true;
                }
                
                console.error(`Erro ao inicializar sessão ${sessionId}:`, error);
                throw error;
            }
        });
    }

    /**
     * Executa uma chamada SSE/streaming para o agente híbrido (Google ADK -> Langchain via A2A + MCPs)
     * @param req Requisição do agente contendo mensagem e contexto
     * @returns Observable que emite chunks de texto conforme são recebidos via SSE
     */
    public runSse(req: AgentRunRequest): Observable<string> {
        const url = this.adkUrl + `/run_sse`;
        this.isLoading.next(true);
        
        return new Observable<string>((observer) => {
            const self = this;
            
            // Usando fetch com EventSource approach para SSE
            // EventSource não suporta POST, então usamos fetch com streaming
            const token = this.getAuthToken();

            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(req),
            })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error(`Http failure response for ${url}: ${response.status} ${response.statusText}`);
                }

                const reader = response.body ? response.body.getReader() : null;
                if (!reader) {
                    throw new Error('Response body is not readable');
                }
                
                const decoder = new TextDecoder('utf-8');
                let buffer = '';

                const processBuffer = (isFinal: boolean = false) => {
                    // SSE usa \n\n para separar eventos, mas alguns eventos podem ter JSON multilinha
                    // Estratégia: encontrar padrões "data: {..." e processar cada um

                    // Primeiro, tentar separar por \n\ndata: (padrão SSE)
                    const eventPattern = /data:\s*(\{[\s\S]*?\})(?=\n\ndata:|\n\n$|$)/g;
                    let match;
                    let lastIndex = 0;

                    while ((match = eventPattern.exec(buffer)) !== null) {
                        const jsonStr = match[1];
                        lastIndex = match.index + match[0].length;

                        try {
                            // Tentar parsear diretamente
                            JSON.parse(jsonStr);
                            self.zone.run(() => observer.next(jsonStr));
                        } catch (e) {
                            // JSON pode ter quebras de linha - tentar limpar
                            try {
                                // Remover quebras de linha extras dentro do JSON
                                const cleanedJson = jsonStr.replace(/\n\s*/g, ' ').replace(/\s+/g, ' ');
                                JSON.parse(cleanedJson);
                                self.zone.run(() => observer.next(cleanedJson));
                            } catch (e2) {
                                // Se ainda falhar, pode ser erro com JSON aninhado
                                // Extrair mensagem de erro manualmente
                                const errorMatch = jsonStr.match(/"error"\s*:\s*"([^]*?)"\s*\}?\s*$/);
                                if (errorMatch) {
                                    const errorMsg = errorMatch[1].replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
                                    const errorJson = JSON.stringify({ error: errorMsg });
                                    console.log('Erro extraído:', errorJson.substring(0, 100) + '...');
                                    self.zone.run(() => observer.next(errorJson));
                                } else {
                                    console.warn('JSON não parseável:', jsonStr.substring(0, 100));
                                }
                            }
                        }
                    }

                    // Manter no buffer apenas o que não foi processado
                    if (lastIndex > 0) {
                        buffer = buffer.substring(lastIndex);
                    }

                    // Se é o processamento final e ainda tem dados, tentar processar
                    if (isFinal && buffer.trim()) {
                        const remaining = buffer.trim();
                        if (remaining.startsWith('data:')) {
                            const jsonStr = remaining.replace(/^data:\s*/, '');
                            try {
                                const cleanedJson = jsonStr.replace(/\n\s*/g, ' ').replace(/\s+/g, ' ');
                                JSON.parse(cleanedJson);
                                self.zone.run(() => observer.next(cleanedJson));
                            } catch (e) {
                                // Extrair erro se possível
                                const errorMatch = jsonStr.match(/"error"\s*:\s*"([^]*?)(?:"\s*\}|$)/);
                                if (errorMatch) {
                                    const errorMsg = errorMatch[1].replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
                                    const errorJson = JSON.stringify({ error: errorMsg });
                                    console.log('Erro final extraído:', errorJson.substring(0, 100) + '...');
                                    self.zone.run(() => observer.next(errorJson));
                                }
                            }
                        }
                        buffer = '';
                    }
                };

                try {
                    while (true) {
                        const { done, value } = await reader.read();
                        
                        if (done) {
                            // Processar qualquer dado restante no buffer (flush final)
                            processBuffer(true);
                            self.isLoading.next(false);
                            self.zone.run(() => observer.complete());
                            break;
                        }
                        
                        // Decodificar e adicionar ao buffer
                        const chunk = decoder.decode(value, { stream: true });
                        buffer += chunk;
                        
                        // Processar buffer
                        processBuffer(false);
                    }
                } catch (error) {
                    self.isLoading.next(false);
                    self.zone.run(() => observer.error(error));
                }
            })
            .catch((error) => {
                self.isLoading.next(false);
                self.zone.run(() => observer.error(error));
            });
            
            // Retornar função de cleanup para cancelar a requisição se necessário
            return () => {
                self.isLoading.next(false);
            };
        });
    }

    /**
     * Gera uma resposta do assistente com base na mensagem do usuário
     * @param userMessage Mensagem do usuário
     * @param userId ID do usuário (opcional)
     * @param sessionId ID da sessão (opcional)
     * @returns Resposta gerada pelo assistente
     * @deprecated Use runSse() para streaming. Este método é mantido para compatibilidade com código legado.
     */
    public async generateAssistantResponse(userMessage: string, userId: string, sessionId: string): Promise<{answer: string, chatid?: string}> {
        return this.withToken(async () => {
            const requestBody = this.buildAgentRequest(userMessage, userId, sessionId, false);
            let response = await this.httpService.post<{answer: string, chatid?: string}>(`${this.chatHistoryUrl}/chat`, requestBody).toPromise();
            return {
                answer: response && response.answer ? response.answer : "",
                chatid: response && response.chatid ? response.chatid : undefined
            };
        });
    }

    /**
     * Método auxiliar para construir a requisição do agente para SSE
     * @param userMessage Mensagem do usuário
     * @param userId ID do usuário (opcional, padrão "user")
     * @param sessionId ID da sessão (opcional, padrão "123")
     * @param state_delta Estado delta para enviar junto com a mensagem
     * @param streaming Se deve usar streaming (opcional, padrão false)
     * @param attachments Lista de arquivos anexados (opcional)
     * @returns Objeto de requisição formatado para o agente
     */
    public buildAgentRequest(
        userMessage: string,
        userId: string = "user",
        sessionId: string = "123",
        state_delta: any = {},
        streaming: boolean = false,
        attachments: FileAttachment[] = []
    ): AgentRunRequest {
        // Construir array de parts
        const parts: Array<{text?: string; inlineData?: {mimeType: string; data: string; displayName?: string}}> = [];

        // Adicionar texto da mensagem se houver
        if (userMessage && userMessage.trim()) {
            parts.push({ text: userMessage });
        }

        // Adicionar anexos como inlineData com displayName para o orquestrador identificar o arquivo
        for (const attachment of attachments) {
            parts.push({
                inlineData: {
                    mimeType: attachment.type,
                    data: attachment.base64,
                    displayName: attachment.name // Nome do arquivo para o orquestrador
                }
            });
        }

        // Se não houver nenhuma part, adicionar texto vazio
        if (parts.length === 0) {
            parts.push({ text: '' });
        }

        return {
            appName: "wings_agent",
            userId: userId,
            sessionId: sessionId,
            newMessage: {
                role: "user",
                parts: parts
            },
            state_delta,
            streaming: streaming
        };
    }

    /**
     * Cria uma nova conversa no backend
     * @param from Origem da conversa (opcional)
     * @returns Promise com o ID da nova conversa criada
     */
    public async createNewConversation(from?: string, profile?: string): Promise<string> {
        return this.withToken(async () => {
            try {
                const response = await this.httpService.post<{
                    success: boolean;
                    conversationId: string;
                    timestamp: string;
                }>(`${this.chatHistoryUrl}/chat-history/new-conversation`, {
                    from: from || 'web',
                    profile: profile
                }).toPromise();
                
                if (!response || !response.conversationId) {
                    throw new Error('ID da conversa não retornado pelo servidor');
                }
                
                console.log(`Nova conversa criada com ID: ${response.conversationId}`);
                return response.conversationId;
            } catch (error) {
                console.error('Erro ao criar nova conversa:', error);
                throw error;
            }
        });
    }

    /**
     * Obtém a lista de resumos de conversas anteriores do usuário
     * @returns Lista de resumos de conversas
     * @note Este endpoint continua usando o LangGraph backend
     */
    public async getConversationSummaries(): Promise<ChatSummary[]> {
        return this.withToken(async () => {
            try {
                const response = await this.httpService.get<{conversations: ChatSummary[]}>(`${this.chatHistoryUrl}/chat-history`).toPromise();
                return response.conversations || [];
            } catch (error) {
                console.error('Erro ao buscar conversas anteriores:', error);
                return [];
            }
        });
    }

    /**
     * Obtém os detalhes completos de uma conversa específica
     * @param conversationId ID da conversa
     * @returns Detalhes completos da conversa
     * @note Este endpoint continua usando o LangGraph backend
     */
    public async getConversationDetails(conversationId: string): Promise<SimplifiedConversation | null> {
        return this.withToken(async () => {
            try {
                const response = await this.httpService.get<{conversation: SimplifiedConversation}>(`${this.chatHistoryUrl}/chat-history/${conversationId}`).toPromise();
                return response.conversation || null;
            } catch (error) {
                console.error(`Erro ao buscar detalhes da conversa ${conversationId}:`, error);
                return null;
            }
        });
    }

    /**
     * Realiza soft delete de uma conversa no backend
     * @param conversationId ID da conversa a ser deletada
     * @returns Promise com true se a exclusão foi realizada com sucesso
     */
    public async deleteConversation(conversationId: string): Promise<boolean> {
        return this.withToken(async () => {
            try {
                const response = await this.httpService.delete<{
                    success: boolean;
                    message: string;
                    timestamp: string;
                }>(`${this.chatHistoryUrl}/chat-history/${conversationId}`, {
                    headers: { 'Content-Type': 'application/json' }
                }).toPromise();

                if (response && response.success) {
                    console.log(`Conversa ${conversationId} deletada com sucesso no backend`);
                    return true;
                }
                return false;
            } catch (error) {
                console.error(`Erro ao deletar conversa ${conversationId}:`, error);
                return false;
            }
        });
    }

    /**
     * Envia o feedback do usuário sobre uma mensagem específica
     * @param feedback Objeto contendo o feedback do usuário
     * @param conversationId ID da conversa
     * @returns Resultado da operação
     * @note Este endpoint continua usando o LangGraph backend
     */
    public async sendFeedback(feedback: ChatFeedback, conversationId: string): Promise<boolean> {
        return this.withToken(async () => {
            try {
                await this.httpService.post(`${this.chatHistoryUrl}/chat-history/feedback`, { 
                    ...feedback, 
                    idConversa: conversationId ,
                }).toPromise();
                return true;
            } catch (error) {
                console.error('Erro ao enviar feedback:', error);
                return false;
            }
        });
    }
}
